import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { CacheKeys } from '@/lib/cache'
import { COVERAGE_TYPE_LABELS, CoverageType } from '@/models/insuranceCoverage'
import { useCreateTestPolicy } from '@/models/qa'

const COVERAGE_OPTIONS = Object.values(CoverageType)

const DEFAULT_COVERAGES = [CoverageType.AUTO_LIABILITY]

/**
 * Dev/staging-only tool: seeds a bound, active policy on this customer's org by
 * walking the real indication -> quote -> bind lifecycle. Repeatable — each
 * submit creates another policy.
 */
export function AddTestPolicyDialog({
  orgId,
  orgName,
  children,
}: {
  orgId: string
  orgName?: string
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<CoverageType[]>(DEFAULT_COVERAGES)
  const [notify, setNotify] = useState(true)
  const queryClient = useQueryClient()
  const mutation = useCreateTestPolicy()

  function toggle(coverage: CoverageType, checked: boolean) {
    setSelected((current) => (checked ? [...current, coverage] : current.filter((c) => c !== coverage)))
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    mutation.mutate(
      { orgId, coverages: selected, notify },
      {
        onSuccess: (policy) => {
          toast.success(`Created test policy ${policy.policyIdShort}`)
          queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId] })
          queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS] })
          setSelected(DEFAULT_COVERAGES)
          setOpen(false)
        },
        onError: () => toast.error('Could not create the test policy.'),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add test policy</DialogTitle>
            <DialogDescription>
              Creates an indication, quotes it, and binds it into an active policy
              {orgName ? ` for ${orgName}` : ''}. Run it again for another policy.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 flex flex-col gap-3">
            {COVERAGE_OPTIONS.map((coverage) => (
              <div key={coverage} className="flex items-center gap-2">
                <Checkbox
                  id={`test-policy-${coverage}`}
                  checked={selected.includes(coverage)}
                  onCheckedChange={(checked) => toggle(coverage, checked === true)}
                />
                <Label htmlFor={`test-policy-${coverage}`} className="font-normal">
                  {COVERAGE_TYPE_LABELS[coverage]}
                </Label>
              </div>
            ))}
          </div>

          <div className="mt-5 flex items-start gap-2 border-t border-neutral-200 pt-4">
            <Checkbox
              id="test-policy-notify"
              checked={notify}
              onCheckedChange={(checked) => setNotify(checked === true)}
            />
            <Label htmlFor="test-policy-notify" className="cursor-pointer flex-col items-start gap-0.5">
              Send notification emails
              <span className="text-muted-foreground text-xs font-normal">
                Emails the business owners the request-to-bind and bound emails, same as a real bind
              </span>
            </Label>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!selected.length}
              loading={mutation.isPending ? 'Creating…' : undefined}
            >
              Create policy
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
